import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import ErrorMessage from '@components/atoms/ErrorMessage';
import Spacing from '@components/atoms/Spacing';
import { Heading1, Paragraph } from '@components/atoms/Typography';
import PageContainer from '@components/molecules/PageContainer';
import BREAKPOINTS from '@lib/constants/breakpoints';
import ROUTES from '@lib/constants/routes';
import crimes from '@lib/data/crimes';
import { GetStaticProps } from 'next';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import slugify from 'slugify';
import styled from 'styled-components';

const Header = styled.header`
  position: relative;
  padding-top: 80px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 50px;
    text-align: center;

    button {
      width: 100%;
    }
  }
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;

  > div {
    padding: 32px;
  }

  > div:first-child {
    max-width: 600px;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;

    > div:nth-child(2) {
      display: none;
    }
    > div:first-child {
      max-width: none;
    }
  }
`;

interface Props {
  models: string[];
}

const Form = styled.form`
  display: grid;
  grid-template-columns: 4fr 2fr;
  grid-gap: 16px;
  align-items: center;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;
  }
`;

const LandingPage = ({ models }: Props) => {
  const router = useRouter();
  const {
    handleSubmit, control, errors, formState,
  } = useForm<{ model: { value: string } }>();

  return (
    <PageContainer
      title="Descubra se sua moto é visada!"
      metaDescription="Com essa ferramenta você consegue ver em quais lugares do estado de São Paulo acontecem os furtos e roubos de moto."
    >
      <Header>
        <HeaderGrid>
          <div>
            <Heading1>Minha moto é visada?</Heading1>
            <Spacing top={2} bottom={4}>
              <Paragraph>
                Veja qual o score da sua moto. Acompanhe em quais lugares do
                estado de São Paulo acontecem os roubos de moto
              </Paragraph>
            </Spacing>
            <Form
              onSubmit={handleSubmit(async (data) => {
                await router.push(
                  `${ROUTES.stolenBikes}/${slugify(data.model.value, {
                    lower: true,
                  })}`,
                );
              })}
            >
              <Controller
                name="model"
                control={control}
                rules={{ required: 'Escolha um modelo' }}
                defaultValue={undefined}
                render={(field) => (
                  <Select
                    {...field}
                    noOptionsMessage={() => 'Não encontrado'}
                    options={models.map((i) => ({ label: i, value: i }))}
                    placeholder="Escolha uma moto"
                  />
                )}
              />
              <PrimaryButton isLoading={formState.isSubmitting} pink>
                Ver Roubos
              </PrimaryButton>
            </Form>
            {(errors.model as { message?: string })?.message && (
              <ErrorMessage>
                {(errors.model as { message?: string }).message}
              </ErrorMessage>
            )}
          </div>
          <div style={{ position: 'relative', height: 'calc(100vh - 80px)' }}>
            <NextImage
              alt="Moto sozinha"
              src="https://images.unsplash.com/photo-1592310294951-bd1b5e709199?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1051&q=80"
              layout="fill"
              quality={100}
              objectFit="cover"
            />
          </div>
        </HeaderGrid>
      </Header>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const models = await crimes.motorCycle.uniqueModels()
  return {
    props: { models },
  };
};

export default LandingPage;
